import {
  BookOpenTextIcon, ChartBarIcon, CoinsIcon, DeviceMobileIcon, HouseIcon, JoystickIcon, PathIcon, RecordIcon, RobotIcon, UserCircleIcon,
  VideoCameraIcon, WalletIcon,
} from '@phosphor-icons/react'
import { parseId } from '@robotensor/ui'
import { DASHBOARD_URL, GUIDE_URL } from './parts.tsx'
import { activeNav, type NavDef } from './nav.tsx'
import type { IdRoute, SiteConfig } from './site.tsx'

/**
 * The contributor app (horizon.robotensor.ai), November 2026 (N1): Home · Robot · Recordings · Wallet in the bar, the
 * phone pill, the bell and the avatar; Account sits behind the avatar. Below 820 px the four move to a bottom bar,
 * the same words as the phone app's tabs. The old words (explore, missions, activity, earnings…) stay as search
 * keywords so the palette still finds the pages they became.
 */
const NAV: readonly NavDef[] = [
  { id: 'home', label: 'Home', to: '/home', icon: HouseIcon, keys: 'g h', group: 'contribute', bottom: true, keywords: ['today', 'next', 'start', 'summary', 'dashboard'], description: 'Your next task, your results and your points' },
  {
    id: 'robot', label: 'Robot', to: '/robot', icon: RobotIcon, keys: 'g c', group: 'contribute', bottom: true, also: ['/explore', '/missions', '/sim/control', '/activity'],
    keywords: ['robot control', 'explore', 'missions', 'tasks', 'search', 'browse', 'bookmarks', 'simulation', 'simulate', 'teleop', 'control', 'robot arm', 'runs', 'my runs', 'activity', 'results', 'history', 'submissions'],
    description: 'Pick a robot task; your phone is the controller',
  },
  {
    id: 'recordings', label: 'Recordings', to: '/recordings', icon: RecordIcon, keys: 'g v', group: 'contribute', bottom: true,
    keywords: ['recording', 'record', 'videos', 'capture', 'episodes', 'labels', 'fix', 'review', 'score', 'egocentric', 'collection requests', 'campaigns'],
    description: 'Your recordings: fixes, labels and results',
  },
  {
    id: 'wallet', label: 'Wallet', to: '/wallet', icon: WalletIcon, keys: 'g r', group: 'contribute', bottom: true, also: ['/earnings', '/rewards'],
    keywords: ['earnings', 'points', 'rewards', 'payouts', 'balance', 'withdraw', 'alpha', 'statements', 'epochs'],
    description: 'Your balance, points and payouts',
  },
  { id: 'account', label: 'Account', to: '/account', icon: UserCircleIcon, keys: 'g p', group: 'you', bar: false, keywords: ['profile', 'devices', 'phones', 'answer my computer', 'sessions', 'payouts', 'notifications', 'privacy'], description: 'Profile, devices, payouts, notifications, privacy' },
  {
    id: 'work', label: 'Work types', to: '/account/work', icon: PathIcon, keys: 'g w', group: 'you', bar: false,
    keywords: ['kinds of work', 'paths', 'join', 'what it takes', 'robot control', 'recording', 'scanning', 'simulation', 'capture'],
    description: 'Robot control, Recording and what each one takes',
  },
]

/**
 * The section a page belongs to. A mission's page sits under Robot control, or under Recordings when it is a
 * recording mission (its path is only known once the mission loads).
 */
export function appSection(pathname: string, nav: readonly NavDef[], missionPath?: string | null): NavDef | undefined {
  const hit = activeNav(pathname, nav)
  if (missionPath === 'ego' && /^\/missions\/[^/]+\/?$/.test(pathname)) return nav.find((n) => n.id === 'recordings') ?? hit
  return hit
}

function routeForId(id: string, _perms: ReadonlySet<string>, userId: string): IdRoute | null {
  const p = parseId(id)
  if (!p || !p.known) return null
  const v = p.value
  switch (p.prefix) {
    case 'usr':
      return v === userId ? { to: '/account', label: 'Open your account' } : null
    case 'msn':
      return { to: '/robot', search: { task: v }, label: 'Open task' }
    case 'cap':
      return { to: `/recordings/${v}`, label: 'Open recording' }
    case 'ctb':
      return { to: `/activity/${v}`, label: 'Open result' }
    case 'dev':
    case 'ses':
      return { to: '/account/devices', label: 'Open devices & sessions' }
    default:
      return null
  }
}

export const APP_SITE: SiteConfig = {
  id: 'app',
  name: 'Horizon',
  nav: NAV,
  groups: [
    { id: 'contribute', label: 'Contribute' },
    { id: 'you', label: 'You' },
  ],
  accountLinks: [
    { key: 'account', label: 'Account', icon: <UserCircleIcon />, keys: 'g p', to: '/account' },
    { key: 'devices', label: 'Devices', icon: <DeviceMobileIcon />, to: '/account/devices' },
    { key: 'payouts', label: 'Payouts', icon: <CoinsIcon />, to: '/account/payouts' },
  ],
  helpLinks: [
    { key: 'guide', label: 'Contributor guide', icon: <BookOpenTextIcon />, href: GUIDE_URL, keywords: ['docs', 'documentation', 'how to', 'getting started', 'faq', 'rules'] },
    { key: 'dashboard', label: 'Rewards dashboard', icon: <ChartBarIcon />, href: DASHBOARD_URL, keywords: ['public', 'rewards', 'leaderboard', 'payouts', 'weeks', 'vault'] },
  ],
  routeForId,
  signIn: {
    title: 'One account for your missions, your phone and your rewards.',
    lede: 'Enter your email and we’ll send a 6-digit code. No password, and the same account works in the Robotensor app on your phone.',
    plate: [
      { label: 'Robot control', icon: JoystickIcon, text: 'Control a simulated robot arm with your phone.' },
      { label: 'Recording', icon: VideoCameraIcon, text: 'Record what you do, hands-free, with your phone.' },
      { label: 'Earn', icon: CoinsIcon, text: 'Accepted work earns points in an auditable ledger, paid out by epoch.' },
    ],
    discord: true,
    google: true,
  },
  onboarding: true,
}
